import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Header } from '../components/layout/Header';
import { StickyNote } from '../components/ui/StickyNote';
import { MusicPlayer } from '../components/ui/MusicPlayer';
import { Trees, Copyright } from 'lucide-react';

export function PublicProfilePage() {
  const { username } = useParams<{ username: string }>();

  return (
    <div className="min-h-screen bg-zinc-50 bg-dots pb-20">
      <Header />

      <main className="max-w-6xl mx-auto px-4 pt-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 relative">

          {/* LEFT COLUMN - Profile */}
          <div className="lg:col-span-5 flex flex-col gap-6">

            {/* Decorative Sticky Note */}
            <div className="hidden lg:block absolute -top-6 -left-12 z-10">
              <StickyNote rotation="-rotate-6">
                "Creativity is Intelligence having fun!"
              </StickyNote>
            </div>

            {/* Profile Card */}
            <Card variant="pop" className="bg-white border-3 border-border p-0 overflow-hidden relative mt-12 lg:mt-0">
              {/* Banner/Pattern Top */}
              <div className="h-32 bg-green-100 bg-[radial-gradient(#4ade80_1px,transparent_1px)] bg-[length:16px_16px] border-b-3 border-border relative">
                <div className="absolute top-4 left-4 opacity-20">
                  <Trees className="w-10 h-10 text-green-800" />
                </div>
              </div>

              {/* Content */}
              <div className="px-8 pb-8 text-center relative">
                {/* Avatar */}
                <div className="w-32 h-32 mx-auto -mt-16 mb-4 rounded-full border-3 border-border bg-orange-100 relative shadow-pop">
                  <img
                    src="/img/profile_intp.png"
                    alt="Avatar"
                    className="w-full h-full object-cover rounded-full"
                    onError={(e) => {
                      // Fallback if image not found
                      (e.target as HTMLImageElement).src = 'https://api.dicebear.com/7.x/avataaars/svg?seed=' + username;
                    }}
                  />
                  <div className="absolute bottom-0 right-0 w-8 h-8 bg-green-400 rounded-full border-2 border-border flex items-center justify-center text-white">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  </div>
                </div>

                <h1 className="font-hand text-4xl font-bold mb-1 text-zinc-900">
                  {username || 'ZineKitty'}
                </h1>
                <p className="font-mono text-sm text-zinc-500 mb-6">@zine_cat_art</p>

                <div className="bg-pink-50 border-2 border-pink-200 rounded-xl p-4 mb-6 relative">
                  <span className="absolute -top-3 -left-2 text-3xl text-pink-300"></span>
                  <p className="font-hand text-xl text-zinc-700 leading-tight">
                    Pastel Artist / Dreamer. <br />
                    Collaging digital worlds with lofi beats & warm tea.
                  </p>
                </div>

                <div className="flex gap-2 justify-center">
                  <Button className="bg-secondary/20 hover:bg-secondary/40 text-secondary-dark border-2 border-secondary font-bold text-sm rounded-full py-1">
                    &lt;&gt; Dev
                  </Button>
                  <Button className="bg-purple-100 hover:bg-purple-200 text-purple-700 border-2 border-purple-300 font-bold text-sm rounded-full py-1">
                    Photos
                  </Button>
                  <Button className="bg-amber-100 hover:bg-amber-200 text-amber-700 border-2 border-amber-300 font-bold text-sm rounded-full py-1">
                    Contact
                  </Button>
                </div>
              </div>
            </Card>

            {/* Music Player Widget */}
            <div className="relative z-20 mx-4 lg:mx-0">
              <MusicPlayer />
            </div>
          </div>


          {/* RIGHT COLUMN - Content Grid */}
          <div className="lg:col-span-7 flex flex-col gap-6">

            {/* Featured Art Portfolio */}
            <Card variant="pop" className="bg-pink-200 border-3 border-border !shadow-sharp hover:!shadow-pop-hover group cursor-pointer relative overflow-hidden">
              <div className="absolute top-4 left-4 w-3 h-3 rounded-full border-2 border-zinc-900 bg-white" />
              <div className="absolute bottom-4 right-4 w-3 h-3 rounded-full border-2 border-zinc-900 bg-white" />

              <div className="flex flex-col md:flex-row items-center gap-6 p-4">
                <div className="w-24 h-24 shrink-0 bg-zinc-900 rounded-full flex items-center justify-center text-white text-4xl rotate-12 group-hover:rotate-0 transition-transform">

                </div>
                <div className="text-left flex-1">
                  <span className="inline-block bg-white/50 border-2 border-zinc-900 px-2 py-0.5 text-xs font-bold rounded mb-2 text-zinc-900">FEATURED</span>
                  <h2 className="font-hand text-4xl font-bold mb-2 text-zinc-900">ART PORTFOLIO</h2>
                  <p className="font-sans text-lg text-zinc-800 leading-snug">
                    Dive into my latest digital masterpieces and sketches.
                  </p>
                </div>
                <div className="text-3xl group-hover:translate-x-2 transition-transform">

                </div>
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Latest Zine */}
              <Card variant="pop" className="bg-secondary border-3 border-border !shadow-sharp hover:scale-[1.02] cursor-pointer">
                <div className="w-12 h-12 bg-white border-2 border-zinc-900 rounded-full flex items-center justify-center text-xl mb-3">

                </div>
                <h3 className="font-hand text-2xl font-bold mb-1">LATEST ZINE</h3>
                <p className="text-zinc-700">Read issue #42 now!</p>
              </Card>

              {/* Live Stream */}
              <Card variant="pop" className="bg-green-200 border-3 border-border !shadow-sharp hover:scale-[1.02] cursor-pointer relative">
                <span className="absolute top-4 right-4 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border border-zinc-900"></span>
                </span>

                <div className="w-12 h-12 bg-white border-2 border-zinc-900 rounded-full flex items-center justify-center text-xl mb-3">

                </div>
                <h3 className="font-hand text-2xl font-bold mb-1">LIVE STREAM</h3>
                <p className="text-zinc-700">Drawing & Chilling</p>
              </Card>
            </div>

            {/* Support */}
            <Card variant="pop" className="bg-purple-200 border-3 border-border !shadow-sharp flex items-center justify-between p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white border-2 border-zinc-900 rounded-full flex items-center justify-center text-purple-500 text-2xl">
                  ♥
                </div>
                <div className="text-left">
                  <h3 className="font-hand text-2xl font-bold">SUPPORT MY ART</h3>
                  <p className="text-zinc-700 text-sm">Help me buy more cat treats & art supplies!</p>
                </div>
              </div>
              <Button className="bg-white border-2 border-zinc-900 rounded-full px-6 font-bold hover:bg-zinc-50 shadow-[2px_2px_0_0_#000]">
                Tip Jar
              </Button>
            </Card>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-20 py-8 text-center text-zinc-400 font-hand text-sm">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Copyright className="w-4 h-4" /> INTPCAT.NET
        </div>
        <p>© 2023 Made with ♥ and chaos.</p>
      </footer>
    </div>
  );
}
